import React from "react";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stack,
  Link,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import { captureContactClick } from "../analytics";
import { ACCENT, ACCENT_SOFT, SERIF, MONO, MEASURE } from "../data/studioStory";

const EMAIL = "hello@skylarkcreations.com";

// The scannable facts, in the order someone deciding whether to email actually
// asks them. Rendered as a definition list rather than prose because this is the
// block both a hurried human and a crawler read first.
const FACTS = [
  ["Status", "Open to a small number of engagements"],
  ["Based", "Colorado. Engagements run remote and async-first"],
  ["Shape", "One human orchestrating a fleet of AI agents, inside your codebase"],
  ["Billing", "Engaged attention, not machine time"],
  ["Start", "An email, then a call once there is a decision to make"],
];

const HOW = [
  [
    "Async by default",
    "No standing meetings. A call gets scheduled when a decision needs one; everything else arrives as artifacts in your inbox. One line from you can re-sort my whole week.",
  ],
  [
    "Verified turnarounds, usually same or next day",
    "You email late about a bug; the root cause, the fix, and the deploy are done by morning. On one engagement, 6–11 verified merges per day was the normal rate over a two-week stretch. That is what one decision-maker plus a fleet of agents is for.",
  ],
  [
    "Nothing you say evaporates",
    "Every round of feedback gets broken into numbered points, and each point gets a written disposition: a faithful paraphrase, a ruling with its reasoning, and a pointer to where it landed. When I disagree I argue it in writing rather than quietly dropping it.",
  ],
  [
    "Spec and a kill-gate before code",
    "We decide and spec first, and the opening milestone is designed to prove or kill the core technical bet in days – before there is anything expensive to be wrong about.",
  ],
  [
    "A second model tries to refute the work before you see it",
    "Release gate: a different AI model family, with no stake in the reasoning that produced the work, is prompted to attack it. Whatever survives gets re-verified against live code. This gate has caught a bypass in a fix that had already been reviewed.",
  ],
  [
    "Scope triage happens in the open",
    "When a client sent a batch of additions before signing, roughly 80% were confirmations or cheap adds and got absorbed at no charge; exactly one item was named as real scope and priced. They saw the split.",
  ],
];

const NOT_FOR = [
  "Standing-meeting cultures. Calls here are decision events, not a daily ritual.",
  "Fixed-scope builds thrown over a wall. Scope lives in a running ledger, and direction changes get absorbed rather than renegotiated.",
  "Staff augmentation. I am not a seat to fill on someone else’s sprint board.",
  "Teams with nobody to react. The loop runs on your reactions – it works best when someone on your side can take a pull request, argue a finding, and merge.",
];

const FAQ = [
  [
    "Is David Kooi available for hire?",
    "Yes. He takes on a small number of client engagements at a time and is currently open to new ones. The way to start is an email to " +
      EMAIL +
      ".",
  ],
  [
    "What kind of work does he take?",
    "Independent AI-orchestration consulting: running a product engagement end to end, from spec through verified, deployed code, with one human orchestrating a fleet of AI agents inside the client's codebase. Also verification and audit of AI-generated code, and review or coaching for a team already building with agents.",
  ],
  [
    "How is the work billed?",
    "On engaged attention rather than machine time. Adding agent capacity never raises the invoice, ramp-up and admin are not billed, and a quiet week legitimately bills near zero.",
  ],
  [
    "How fast does work actually ship?",
    "Turnarounds are usually same-day or next-day, and verified rather than claimed. On one engagement, 6-11 verified merges per day was the normal rate over a two-week stretch.",
  ],
  [
    "Is this an agency, or one person?",
    "One person with an unusually good harness. There is no bench of named humans, and every client-facing judgment funnels through one head. That is a real constraint, not a marketing position.",
  ],
  [
    "When is this a bad fit?",
    "Standing-meeting cultures, fixed-scope builds thrown over a wall, and staff augmentation. It also depends on the client being able to react: a quiet client starves the engagement.",
  ],
];

function Eyebrow({ children }) {
  return (
    <Typography
      component="p"
      sx={{
        fontFamily: MONO,
        fontSize: "0.72rem",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "text.secondary",
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function SectionHeading({ children }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: SERIF,
        fontWeight: 600,
        fontSize: { xs: "1.6rem", md: "2rem" },
        color: "text.primary",
        mb: 2.5,
      }}
    >
      {children}
    </Typography>
  );
}

function HirePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <PageTransition>
      <Helmet>
        <title>Working together – David Kooi</title>
        <link rel="canonical" href="https://davidkooi.com/hire/" />
        <meta
          name="description"
          content="David Kooi takes on a small number of client engagements at a time and is open to new ones. Independent AI-orchestration consulting: async-first, verified turnarounds, billed on engaged attention rather than machine time."
        />
        <meta property="og:title" content="Working together – David Kooi" />
        <meta
          property="og:description"
          content="Open to a small number of engagements. One human orchestrating a fleet of AI agents, inside your codebase. Async-first, billed on engaged attention."
        />
        <meta property="og:url" content="https://davidkooi.com/hire/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Working together – David Kooi" />
        <meta
          name="twitter:description"
          content="Open to a small number of engagements. Async-first, verified turnarounds, billed on engaged attention."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          })}
        </script>
      </Helmet>

      <Box
        sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}
      >
        <Navigation />

        {/* Landmark for the header skip link; content is not re-indented
            so the diff stays two lines. */}
        <Box component="main" id="main">

        {/* ============ Hero + facts panel: two columns, not a centered stack ============ */}
        <Box
          sx={{
            background: `radial-gradient(900px 400px at 15% -10%, ${alpha(
              ACCENT,
              isDark ? 0.12 : 0.09
            )}, transparent 62%), ${theme.palette.background.default}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 9 }, pb: { xs: 5, md: 8 } }}>
            <ScrollReveal>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
                  gap: { xs: 4, md: 7 },
                  alignItems: "start",
                }}
              >
                <Box>
                  <Eyebrow>Working together</Eyebrow>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "2.1rem", md: "3rem" },
                      lineHeight: 1.12,
                      color: "text.primary",
                    }}
                  >
                    I take on a few engagements at a time.
                  </Typography>
                  <Typography
                    sx={{
                      mt: 2.5,
                      color: "text.secondary",
                      lineHeight: 1.75,
                      fontSize: { xs: "1rem", md: "1.08rem" },
                      maxWidth: MEASURE,
                    }}
                  >
                    What you get is one person with an unusually good harness –
                    me, orchestrating a fleet of AI agents inside your codebase and
                    your working practice. There is no bench of engineers behind me,
                    and every judgment you see funnels through one head. That is the
                    real shape of it, and it is worth knowing before you write.
                  </Typography>

                  <Button
                    href={`mailto:${EMAIL}`}
                    onClick={() => captureContactClick("hire-hero")}
                    disableElevation
                    sx={{
                      mt: 3.5,
                      backgroundColor: ACCENT,
                      // Near-black, not white: white on ACCENT is 2.87:1, under the
                      // 4.5:1 AA floor. This is the value palette.accent.contrastText
                      // already carries for exactly this reason.
                      color: "#1a1a1a",
                      px: 3,
                      py: 1.25,
                      minHeight: 44,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "1rem",
                      boxShadow: `0 8px 24px ${alpha(ACCENT, 0.35)}`,
                      "&:hover": {
                        backgroundColor: ACCENT,
                        boxShadow: `0 12px 30px ${alpha(ACCENT, 0.45)}`,
                      },
                    }}
                  >
                    Email me – {EMAIL}
                  </Button>
                </Box>

                {/* Facts panel */}
                <Box
                  component="dl"
                  sx={{
                    m: 0,
                    p: { xs: 2.5, md: 3 },
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      isDark ? 0.4 : 0.7
                    ),
                  }}
                >
                  {FACTS.map(([term, value], i) => (
                    <Box
                      key={term}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "92px 1fr" },
                        gap: { xs: 0.25, sm: 2 },
                        py: 1.5,
                        borderTop:
                          i === 0 ? "none" : `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        component="dt"
                        sx={{
                          fontFamily: MONO,
                          fontSize: "0.7rem",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          pt: { sm: "3px" },
                        }}
                      >
                        {term}
                      </Typography>
                      <Typography
                        component="dd"
                        sx={{
                          m: 0,
                          color: "text.primary",
                          fontSize: "0.95rem",
                          lineHeight: 1.6,
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        {/* ============ How an engagement runs ============ */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <ScrollReveal>
            <Box sx={{ maxWidth: MEASURE }}>
              <Eyebrow>How it runs</Eyebrow>
              <SectionHeading>What the working relationship looks like</SectionHeading>
            </Box>
          </ScrollReveal>

          <Box
            sx={{
              mt: 4,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              columnGap: 6,
              rowGap: 4,
            }}
          >
            {HOW.map(([title, body], i) => (
              <ScrollReveal key={title} delay={0.05 * i}>
                <Box>
                  <Typography
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.05rem",
                      color: "text.primary",
                      mb: 1,
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{ color: "text.secondary", lineHeight: 1.75, fontSize: "0.97rem", maxWidth: MEASURE }}
                  >
                    {body}
                  </Typography>
                </Box>
              </ScrollReveal>
            ))}
          </Box>
        </Container>

        {/* ============ Billing ============ */}
        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(ACCENT, isDark ? 0.05 : 0.035),
          }}
        >
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
            <ScrollReveal>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
                  gap: { xs: 3, md: 7 },
                  alignItems: "start",
                }}
              >
                <Box>
                  <Eyebrow>What it costs</Eyebrow>
                  <SectionHeading>Billed on engaged attention</SectionHeading>
                </Box>
                <Box>
                  <Typography
                    sx={{ color: "text.secondary", lineHeight: 1.8, fontSize: "1.02rem", maxWidth: MEASURE }}
                  >
                    Not machine time. Adding agent capacity never raises your invoice
                    – the leverage is mine to supply, not yours to fund. Ramp-up
                    and admin are not billed. A quiet week legitimately bills near zero,
                    and if an engagement is idling because the person I need is consumed
                    elsewhere, I will tell you it is idling rather than manufacture
                    activity to fill an invoice.
                  </Typography>
                  <Typography
                    sx={{ mt: 2, color: "text.secondary", lineHeight: 1.8, fontSize: "1.02rem", maxWidth: MEASURE }}
                  >
                    Scope lives in a running ledger rather than a fixed deliverable
                    list, so a change in direction gets absorbed instead of triggering a
                    change order.
                  </Typography>
                </Box>
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        {/* ============ Who this isn't for ============ */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <ScrollReveal>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
                gap: { xs: 3, md: 7 },
                alignItems: "start",
              }}
            >
              <Box>
                <Eyebrow>Fit</Eyebrow>
                <SectionHeading>Who this isn’t for</SectionHeading>
                <Typography
                  sx={{ color: "text.secondary", lineHeight: 1.75, fontSize: "0.97rem", maxWidth: MEASURE }}
                >
                  Named plainly, so nobody wastes a month finding out. If one of these
                  is the shape you need, there are good shops for it – I am not
                  one.
                </Typography>
              </Box>
              <Stack spacing={2}>
                {NOT_FOR.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      gap: 1.75,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        mt: "9px",
                        flex: "0 0 auto",
                        width: 18,
                        height: 1,
                        backgroundColor: alpha(theme.palette.text.secondary, 0.5),
                      }}
                    />
                    <Typography
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.7,
                        fontSize: "0.97rem",
                        maxWidth: MEASURE,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </ScrollReveal>
        </Container>

        {/* ============ Start ============ */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
            <ScrollReveal>
              <Box
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 3,
                  border: `1px solid ${alpha(ACCENT, 0.35)}`,
                  backgroundColor: ACCENT_SOFT,
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: SERIF,
                    fontWeight: 600,
                    fontSize: { xs: "1.6rem", md: "2rem" },
                    color: "text.primary",
                  }}
                >
                  How to start
                </Typography>
                <Typography
                  sx={{
                    mt: 1.5,
                    color: "text.secondary",
                    lineHeight: 1.75,
                    maxWidth: MEASURE,
                  }}
                >
                  Send an email describing what you are trying to build or fix. You do
                  not need a spec – a paragraph is enough to tell whether this is
                  a fit. If it is, the first milestone is designed to prove or kill the
                  core technical bet before there is anything expensive to be wrong
                  about.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 3.5, alignItems: { sm: "center" } }}
                >
                  <Button
                    href={`mailto:${EMAIL}`}
                    onClick={() => captureContactClick("hire-footer")}
                    disableElevation
                    sx={{
                      backgroundColor: ACCENT,
                      // Near-black, not white: white on ACCENT is 2.87:1, under the
                      // 4.5:1 AA floor. This is the value palette.accent.contrastText
                      // already carries for exactly this reason.
                      color: "#1a1a1a",
                      px: 3,
                      py: 1.25,
                      minHeight: 44,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "1rem",
                      boxShadow: `0 8px 24px ${alpha(ACCENT, 0.35)}`,
                      "&:hover": {
                        backgroundColor: ACCENT,
                        boxShadow: `0 12px 30px ${alpha(ACCENT, 0.45)}`,
                      },
                    }}
                  >
                    {EMAIL}
                  </Button>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.95rem", maxWidth: MEASURE }}>
                    Or read{" "}
                    <Link
                      component={RouterLink}
                      to="/studio"
                      color="primary"
                      underline="hover"
                    >
                      how the studio runs
                    </Link>{" "}
                    first – it is the same operating system your engagement would
                    run on.
                  </Typography>
                </Stack>
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        </Box>

        <Footer />
      </Box>
    </PageTransition>
  );
}

export default HirePage;
